import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, LogOut, GraduationCap, Calendar, FileDown, BookOpen, Video, ExternalLink, Sparkles, Clock, Plus, Lock, CreditCard, CheckCircle2, Package, Wallet } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { portalApi } from '@/lib/portalApi';
import NotificationBell from '../../components/NotificationBell';
import ChangePasswordModal from '../../components/ChangePasswordModal';
import PaymentCurrencyModal from '../../components/portal/PaymentCurrencyModal';
import StudentLearningPlanSection from '../../components/portal/StudentLearningPlanSection';
import StudentPTESection from '../../components/portal/StudentPTESection';
import StudentIeltsSection from '../../components/portal/StudentIeltsSection';
import StudentCoursePaymentsSection from '../../components/portal/StudentCoursePaymentsSection';
import SelectiveEntryHub from '../../components/portal/SelectiveEntryHub';
import StudentRevisionTasks from '../../components/portal/StudentRevisionTasks';
import UpcomingSessionsWidget from '../../components/portal/UpcomingSessionsWidget';
import CollaborationHub from '../../components/portal/CollaborationHub';
import WinhousePanel from '../../components/portal/WinhousePanel';
import { isEligibleSelectiveProgram } from '../../lib/selectiveEligibility';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const _ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

/**
 * Phase 4 — Student dashboard. Single page with tab-based sections:
 *  Overview · Classes · Recaps · Resources
 *
 * All data is scoped server-side via /api/me/student/* — no email leaks across
 * accounts.
 */
const Tab = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`h-9 px-4 rounded-full text-xs font-bold uppercase tracking-wider transition ${active ? 'bg-black text-white' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-100'}`}>
    {children}
  </button>
);

const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch (_e) { return iso || '—'; } };

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-800',
  approved: 'bg-emerald-100 text-emerald-800',
  scheduled: 'bg-blue-100 text-blue-800',
  live: 'bg-emerald-500 text-white animate-pulse',
  completed: 'bg-gray-100 text-gray-600',
  cancelled: 'bg-red-100 text-red-800',
  missed: 'bg-orange-100 text-orange-800',
};

const fmtMoney = (cents, currency = 'AUD') => `${currency} ${((cents || 0) / 100).toFixed(2)}`;

const initiateBookingPayment = async ({ booking_id, preferred_currency }) => {
  try {
    const r = await _ax.post('/api/payments/paystack/initiate', { booking_id, preferred_currency });
    if (r.data?.status === 'paid_by_credit') {
      toast.success(r.data?.session_credits_used ? 'Booking paid with a prepaid session' : 'Booking paid via credit/coupon');
      window.location.reload();
      return;
    }
    if (r.data?.authorization_url) {
      window.location.href = r.data.authorization_url;
    } else {
      toast.error('Could not start checkout');
    }
  } catch (e) {
    toast.error(e.response?.data?.detail || 'Payment initiation failed');
  }
};

const ClassCard = ({ c, onPay }) => {
  const needsPayment = c.payment_required && c.payment_status !== 'paid';
  return (
    <div className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-xl hover:border-red-200 transition" data-testid={`class-row-${c.booking_id}`}>
      <div className="h-10 w-10 rounded-lg bg-black text-[#D3D86F] flex items-center justify-center flex-shrink-0">
        <Video className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold text-gray-900 truncate">{c.subject}</div>
        <div className="text-[11px] text-gray-500 flex items-center gap-2 flex-wrap">
          <Clock className="h-3 w-3" /> {fmt(c.start_time)}
          <span className="text-gray-300">·</span>
          <span>{c.tutor_name}</span>
          {c.status && <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ml-1 ${STATUS_COLORS[c.status] || 'bg-gray-100 text-gray-700'}`}>{c.status}</span>}
          {needsPayment && (
            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 ml-1" data-testid={`pay-pending-${c.booking_id}`}>
              Payment {c.payment_status === 'pending' ? 'pending' : 'required'}
            </span>
          )}
          {c.payment_status === 'paid' && (
            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 ml-1 inline-flex items-center gap-0.5">
              <CheckCircle2 className="h-2.5 w-2.5" /> Paid {c.amount_paid_cents ? fmtMoney(c.amount_paid_cents, c.currency) : ''}
            </span>
          )}
        </div>
      </div>
      {needsPayment ? (
        <button onClick={() => onPay(c)}
          className="h-9 px-4 rounded-full bg-amber-500 hover:bg-amber-600 text-black text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5"
          data-testid={`pay-now-${c.booking_id}`}>
          <CreditCard className="h-3 w-3" />
          Pay {fmtMoney(c.amount_due_cents || c.lesson_price_cents, 'AUD')}
        </button>
      ) : c.join_path && ['scheduled', 'live', 'approved', 'confirmed'].includes(c.status) ? (
        <a href={c.join_path} target="_blank" rel="noopener noreferrer"
          className="h-9 px-4 rounded-full bg-red-700 hover:bg-red-800 text-white text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5"
          data-testid={`class-join-${c.booking_id}`}>
          <ExternalLink className="h-3 w-3" /> Join
        </a>
      ) : null}
    </div>
  );
};

const RecapCard = ({ r }) => (
  <div className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-xl group hover:border-red-200 transition" data-testid={`recap-row-${r.recap_id}`}>
    <div className="h-10 w-10 rounded-lg bg-black text-[#D3D86F] flex items-center justify-center flex-shrink-0">
      <FileDown className="h-4 w-4" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-sm font-bold text-gray-900 truncate flex items-center gap-1.5">
        {r.filename}
        {r.sections_used?.ai && (
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-black text-[#D3D86F] text-[9px] font-bold uppercase tracking-wider">
            <Sparkles className="h-2.5 w-2.5" /> AI
          </span>
        )}
      </div>
      <div className="text-[11px] text-gray-500">{r.lesson_track || r.subject} · {fmt(r.generated_at)}</div>
    </div>
    {r.download_url && (
      <a href={r.download_url} target="_blank" rel="noopener noreferrer"
        className="h-9 px-4 rounded-full bg-black hover:bg-gray-800 text-white text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5"
        data-testid={`recap-download-${r.recap_id}`}>
        Download
      </a>
    )}
  </div>
);

const Empty = ({ children, testid }) => (
  <div className="text-center text-xs text-gray-400 italic py-10" data-testid={testid}>{children}</div>
);

const StudentDashboardPage = () => {
  const navigate = useNavigate();
  const { user, logout, loading } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('overview');
  const [loadingData, setLoadingData] = useState(true);
  const [resources, setResources] = useState([]);
  const [pwOpen, setPwOpen] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [credits, setCredits] = useState({ session_credits_balance: 0 });
  const [payModal, setPayModal] = useState(null);          // { type:'booking'|'intensive', booking?, amountAudCents, label }
  const [intensiveBusy, setIntensiveBusy] = useState(false);
  const [myTutors, setMyTutors] = useState([]);
  const [planProgram, setPlanProgram] = useState(null);
  const [enrolledPrograms, setEnrolledPrograms] = useState([]);

  const downloadTimetablePdf = async () => {
    try {
      const r = await _ax.get('/api/me/student/timetable.pdf', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = 'my-timetable.pdf';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Could not download timetable');
    }
  };

  const refresh = useCallback(async () => {
    try {
      setData(await portalApi.myDashboard());
      try {
        const pr = await _ax.get('/api/public/pricing');
        setPricing(pr.data || null);
      } catch (_e) {}
      try {
        const cr = await _ax.get('/api/me/student/credits');
        setCredits(cr.data || { session_credits_balance: 0 });
      } catch (_e) {}
      try {
        const tu = await _ax.get('/api/me/student/my-tutors');
        setMyTutors(tu.data?.items || []);
      } catch (_e) {}
      try {
        const lp = await _ax.get('/api/me/student/learning-plan');
        setPlanProgram(lp.data?.plan?.program || null);
      } catch (_e) {}
      try {
        const pr = await _ax.get('/api/me/student/programs');
        setEnrolledPrograms(pr.data?.items || []);
      } catch (_e) {}
    } catch (_e) {} finally { setLoadingData(false); }
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/student/login', { replace: true }); return; }
    const role = (user.role || '').toLowerCase();
    if (role === 'tutor') { navigate('/tutor/dashboard', { replace: true }); return; }
    if (role === 'admin' || role === 'owner') { navigate('/admin/dashboard', { replace: true }); return; }
    if (['staff', 'support', 'finance'].includes(role)) { navigate('/staff/dashboard', { replace: true }); return; }
    refresh();
  }, [loading, user, navigate, refresh]);

  useEffect(() => {
    if (tab === 'resources') {
      portalApi.myStudentResources().then((r) => setResources(r.items || [])).catch(() => {});
    }
  }, [tab]);

  if (loading || loadingData || !user) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]"><Loader2 className="h-6 w-6 animate-spin text-red-700" /></div>;
  }

  const onPayBooking = (c) => {
    setPayModal({
      type: 'booking',
      booking: c,
      amountAudCents: c.amount_due_cents || c.lesson_price_cents || 0,
      label: `Pay for ${c.subject}`,
    });
  };

  const onConfirmPay = async ({ preferred_currency }) => {
    if (!payModal) return;
    if (payModal.type === 'booking') {
      await initiateBookingPayment({ booking_id: payModal.booking.booking_id, preferred_currency });
    } else if (payModal.type === 'intensive') {
      try {
        const r = await _ax.post('/api/payments/paystack/intensive/initiate', { preferred_currency });
        if (r.data?.authorization_url) {
          window.location.href = r.data.authorization_url;
        } else {
          toast.error('Could not start intensive checkout');
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Intensive package checkout failed');
      }
    }
  };

  const startIntensivePurchase = () => {
    if (!pricing) { toast.error('Pricing not loaded'); return; }
    setIntensiveBusy(true);
    setPayModal({
      type: 'intensive',
      amountAudCents: pricing.intensive_price_aud_cents || 25000,
      label: `Intensive Package · ${pricing.intensive_credits || 4} sessions`,
    });
    setTimeout(() => setIntensiveBusy(false), 200);
  };

  const totals = data?.totals || {};
  const upcoming = data?.upcoming_classes || [];
  const completed = data?.completed_classes || [];
  const recaps = data?.recent_recaps || [];

  const Stat = ({ label, value, testid }) => (
    <div className="bg-white border border-gray-200 rounded-2xl p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[2px] text-gray-500 font-bold">{label}</div>
      <div className="text-2xl font-bold text-gray-900 mt-1">{value ?? 0}</div>
    </div>
  );

  return (
    <>
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="student-dashboard">
      <header className="bg-black text-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <GraduationCap className="h-5 w-5 text-[#D3D86F]" />
            <div>
              <div className="text-[10px] uppercase tracking-[3px] text-[#D3D86F] font-bold">WINHOUSE STUDENT PORTAL</div>
              <div className="text-sm font-bold">{user.name || user.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell testidPrefix="student-" />
            <button onClick={() => navigate('/student/book')}
              className="h-9 px-4 rounded-full bg-red-700 hover:bg-red-800 text-white text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5"
              data-testid="nav-book-class">
              <Plus className="h-3 w-3" /> Book a Class
            </button>
            <button onClick={() => setPwOpen(true)}
              className="h-9 px-3 rounded-full bg-white/10 hover:bg-white/20 text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5"
              data-testid="nav-change-password">
              <Lock className="h-3 w-3" /> Password
            </button>
            <button onClick={() => { logout(); navigate('/student/login'); }}
              className="h-9 px-4 rounded-full bg-white/10 hover:bg-white/20 text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5"
              data-testid="portal-logout">
              <LogOut className="h-3 w-3" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Stat label="Classes" value={totals.classes} testid="stat-classes" />
          <Stat label="Recaps" value={totals.recaps} testid="stat-recaps" />
          <Stat label="Resources" value={totals.resources} testid="stat-resources" />
          <Stat label="Upcoming" value={upcoming.length} testid="stat-upcoming" />
          <Stat label="Prepaid Sessions" value={credits.session_credits_balance || 0} testid="stat-prepaid-sessions" />
        </div>

        {/* Phase 5D — Intensive Package promo (only when student has no remaining credits) */}
        {(credits.session_credits_balance || 0) === 0 && pricing && (
          <div className="bg-gradient-to-br from-amber-50 to-amber-100 border-2 border-amber-300 rounded-2xl p-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4" data-testid="intensive-package-tile">
            <div className="flex items-start gap-3">
              <div className="h-12 w-12 rounded-xl bg-amber-500 text-white flex items-center justify-center shrink-0">
                <Package className="h-6 w-6" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[2px] text-amber-700 font-bold">Save with the Intensive Package</div>
                <div className="text-base font-bold text-gray-900 mt-0.5">{pricing.intensive_credits} × 60-min sessions for AUD {pricing.intensive_price_aud?.toFixed(2)}</div>
                <div className="text-xs text-amber-800 mt-1">Includes unlimited writing feedback · priority scheduling · mock test assessments</div>
              </div>
            </div>
            <button onClick={startIntensivePurchase} disabled={intensiveBusy}
              className="h-11 px-6 rounded-full bg-black hover:bg-gray-800 text-white text-xs font-bold uppercase tracking-wider flex items-center gap-2 shrink-0 disabled:opacity-50"
              data-testid="buy-intensive-btn">
              <Wallet className="h-4 w-4" /> Buy Intensive · AUD {pricing.intensive_price_aud?.toFixed(2)}
            </button>
          </div>
        )}
        {(credits.session_credits_balance || 0) > 0 && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex items-center gap-3" data-testid="prepaid-credits-banner">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            <div className="text-sm text-emerald-900">
              You have <strong>{credits.session_credits_balance}</strong> prepaid sessions remaining. They'll be applied automatically when you book.
            </div>
          </div>
        )}

        {/* Phase 6E — Selective Entry Hub (only for selective/VCE/scholarship students) */}
        <SelectiveEntryHub planProgram={planProgram} />

        {/* Phase 8.2 — Revision tasks assigned by tutor */}
        <StudentRevisionTasks />

        {/* Phase 5F — Learning Plan section */}
        <StudentLearningPlanSection />

        {/* Phase-P — Course payment status (per-course PAID/PARTIAL/DUE) */}
        <StudentCoursePaymentsSection />

        {/* PTE Phase 1A — PTE Academic dashboard section */}
        <StudentPTESection />
        <StudentIeltsSection />

        <UpcomingSessionsWidget role="student" />

        {/* Phase 5E — My Tutors */}
        {myTutors.length > 0 && (
          <section className="bg-white border border-gray-200 rounded-2xl p-5" data-testid="my-tutors-section">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="text-xs uppercase tracking-wider text-gray-500 font-bold">My Tutors · {myTutors.length}</div>
              <div className="flex gap-2">
                <button onClick={() => navigate('/student/learning')}
                  className="text-xs px-3 py-1.5 rounded-full bg-red-700 hover:bg-red-800 text-white inline-flex items-center gap-1.5"
                  data-testid="open-learning-centre">
                  <Sparkles className="h-3.5 w-3.5" /> Open Learning Centre
                </button>
                <button onClick={downloadTimetablePdf}
                  className="text-xs px-3 py-1.5 rounded-full border border-gray-200 hover:bg-gray-50 inline-flex items-center gap-1.5"
                  data-testid="download-timetable-pdf">
                  <FileDown className="h-3.5 w-3.5" /> Timetable PDF
                </button>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {myTutors.map((t) => (
                <div key={t.id} className="border border-gray-200 rounded-xl p-3" data-testid={`my-tutor-${t.id}`}>
                  <div className="text-sm font-bold text-gray-900">{t.tutor_name || t.tutor_email}</div>
                  <div className="text-[11px] text-gray-500">{t.tutor_email}</div>
                  <div className="mt-1.5 inline-flex items-center px-2 py-0.5 rounded-full bg-blue-50 text-blue-800 text-[10px] font-bold">{t.subject_area}</div>
                  {t.program && <span className="ml-1.5 text-[10px] text-gray-500">· {t.program}</span>}
                </div>
              ))}
            </div>
          </section>
        )}

        <div className="flex items-center gap-2 flex-wrap" data-testid="student-tabs">
          <Tab active={tab === 'overview'} onClick={() => setTab('overview')} testid="tab-overview">Overview</Tab>
          <Tab active={tab === 'classes'} onClick={() => setTab('classes')} testid="tab-classes">Classes</Tab>
          <Tab active={tab === 'recaps'} onClick={() => setTab('recaps')} testid="tab-recaps">Recaps</Tab>
          <Tab active={tab === 'resources'} onClick={() => setTab('resources')} testid="tab-resources">Resources</Tab>
          <Tab active={tab === 'hub'} onClick={() => setTab('hub')} testid="tab-hub">Hub</Tab>
          <Tab active={tab === 'winhouse'} onClick={() => setTab('winhouse')} testid="tab-winhouse">Winhouse</Tab>
        </div>

        {tab === 'overview' && (
          <div className="grid lg:grid-cols-2 gap-6">
            <section className="bg-white border border-gray-200 rounded-2xl p-5" data-testid="section-upcoming">
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="h-4 w-4 text-red-700" />
                <div className="text-xs uppercase tracking-wider text-gray-500 font-bold">Next up</div>
              </div>
              {upcoming.length === 0 && <Empty testid="empty-upcoming">No upcoming classes yet.</Empty>}
              <div className="space-y-2">{upcoming.slice(0, 3).map((c) => <ClassCard key={c.booking_id} c={c} onPay={onPayBooking} />)}</div>
            </section>
            <section className="bg-white border border-gray-200 rounded-2xl p-5" data-testid="section-recent-recaps">
              <div className="flex items-center gap-2 mb-3">
                <FileDown className="h-4 w-4 text-red-700" />
                <div className="text-xs uppercase tracking-wider text-gray-500 font-bold">Recent recaps</div>
              </div>
              {recaps.length === 0 && <Empty testid="empty-recaps">No lesson recaps yet.</Empty>}
              <div className="space-y-2">{recaps.slice(0, 3).map((r) => <RecapCard key={r.recap_id} r={r} />)}</div>
            </section>
          </div>
        )}

        {tab === 'classes' && (
          <div className="space-y-6">
            <section className="bg-white border border-gray-200 rounded-2xl p-5" data-testid="section-all-upcoming">
              <div className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-3">Upcoming · {upcoming.length}</div>
              {upcoming.length === 0 && <Empty>No upcoming classes.</Empty>}
              <div className="space-y-2">{upcoming.map((c) => <ClassCard key={c.booking_id} c={c} onPay={onPayBooking} />)}</div>
            </section>
            <section className="bg-white border border-gray-200 rounded-2xl p-5" data-testid="section-completed">
              <div className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-3">Completed · {completed.length}</div>
              {completed.length === 0 && <Empty>No completed classes yet.</Empty>}
              <div className="space-y-2">{completed.map((c) => <ClassCard key={c.booking_id} c={c} onPay={onPayBooking} />)}</div>
            </section>
          </div>
        )}

        {tab === 'recaps' && (
          <section className="bg-white border border-gray-200 rounded-2xl p-5" data-testid="section-recaps">
            <div className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-3">All recaps · {recaps.length}</div>
            {recaps.length === 0 && <Empty>No recaps yet.</Empty>}
            <div className="space-y-2">{recaps.map((r) => <RecapCard key={r.recap_id} r={r} />)}</div>
          </section>
        )}

        {tab === 'resources' && (
          <div className="space-y-4">
            {(isEligibleSelectiveProgram(planProgram) || enrolledPrograms.some(p => isEligibleSelectiveProgram(p.name))) && (
              <section
                className="bg-black text-white border border-[#D3D86F]/30 rounded-2xl p-5 shadow-lg overflow-hidden relative"
                data-testid="section-selective-learning-centre">
                <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
                  background: 'radial-gradient(60% 40% at 20% 0%, rgba(220,38,38,0.35) 0%, rgba(220,38,38,0) 60%), radial-gradient(50% 30% at 90% 100%, rgba(211,216,111,0.20) 0%, rgba(211,216,111,0) 70%)',
                }} />
                <div className="relative">
                  <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#D3D86F]">Winhouse · Enrolled Access</div>
                      <h3 className="text-lg font-bold mt-1 flex items-center gap-2">
                        <GraduationCap className="h-5 w-5 text-[#D3D86F]" /> Selective Entry Learning Centre
                      </h3>
                      <p className="text-white/70 text-xs mt-0.5">All practice topics, mocks, downloads and daily challenge — unlocked with your plan.</p>
                    </div>
                    <button
                      onClick={() => navigate('/student/learning')}
                      className="h-10 px-5 rounded-full bg-[#D3D86F] hover:bg-[#c4ca5e] text-black font-bold text-xs uppercase tracking-wider inline-flex items-center gap-1.5"
                      data-testid="open-selective-learning-centre">
                      Open Selective Entry Learning Centre <ExternalLink className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {[
                      { to: '/student/learning?track=mathematics', icon: BookOpen, title: 'Mathematics Practice', sub: 'Topics · quizzes', t: 'sel-tile-maths' },
                      { to: '/student/learning?track=english', icon: BookOpen, title: 'English Practice', sub: 'Reading · writing', t: 'sel-tile-english' },
                      { to: '/student/exams', icon: GraduationCap, title: 'Mock Exams', sub: '90+ full papers', t: 'sel-tile-mocks' },
                      { to: '/resources/selective-entry', icon: FileDown, title: 'Downloads', sub: 'Formulas · vocab', t: 'sel-tile-downloads' },
                      { to: '/dashboard#daily-challenge', icon: Sparkles, title: 'Daily Challenge', sub: '+5 XP · today', t: 'sel-tile-daily' },
                      { to: '/student/learning?resume=1', icon: Video, title: 'Continue Learning', sub: 'Resume topics', t: 'sel-tile-continue' },
                    ].map((c) => (
                      <button key={c.t} onClick={() => navigate(c.to)} data-testid={c.t}
                        className="text-left bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#D3D86F]/40 rounded-xl p-3 transition-colors">
                        <div className="w-8 h-8 rounded-lg bg-[#D3D86F]/20 text-[#D3D86F] flex items-center justify-center mb-2">
                          <c.icon className="h-4 w-4" />
                        </div>
                        <div className="text-sm font-bold">{c.title}</div>
                        <div className="text-[10px] text-white/60 mt-0.5">{c.sub}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </section>
            )}

            <section className="bg-white border border-gray-200 rounded-2xl p-5" data-testid="section-resources">
              <div className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-3">Resources shared with you · {resources.length}</div>
              {resources.length === 0 && <Empty>No resources shared yet.</Empty>}
              <div className="space-y-2">
                {resources.map((res) => (
                  <div key={res.resource_id || res.id || res.public_id} className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-100 rounded-lg" data-testid={`resource-row-${res.resource_id || res.id || res.public_id}`}>
                    <BookOpen className="h-4 w-4 text-red-700 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-bold text-gray-900 truncate">{res.filename}</div>
                      <div className="text-[10px] text-gray-500">{fmt(res.uploaded_at)}</div>
                    </div>
                    {res.secure_url && (
                      <a href={res.secure_url} target="_blank" rel="noopener noreferrer"
                        className="h-8 px-3 rounded-lg bg-black text-white text-[10px] uppercase tracking-wider font-bold hover:bg-gray-800">Open</a>
                    )}
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}

        {tab === 'hub' && (
          <div data-testid="tab-content-hub">
            <CollaborationHub />
          </div>
        )}

        {tab === 'winhouse' && (
          <div data-testid="tab-content-winhouse">
            <WinhousePanel />
          </div>
        )}
      </main>
    </div>
    <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />
    {payModal && (
      <PaymentCurrencyModal
        amountAudCents={payModal.amountAudCents}
        label={payModal.label}
        onConfirm={onConfirmPay}
        onClose={() => setPayModal(null)}
      />
    )}
    </>
  );
};

export default StudentDashboardPage;
